/* ═══════════════════════════════════════════════════════════════════════════
   Fechamento Mensal — Tipos (contrato real do backend)

   Espelha os DTOs de `backend/apps/api/src/features/closing/presentation/
   http/dto/closing.dto.ts` (ClosingModule, rota /api/v1/closing).

   Fontes:
   - FechamentoPeriodoDto / PaginatedFechamentoPeriodoResponseDto
   - DashboardDto / DashboardPeriodoDto / DashboardModuloDto / CriticalPendingIssueDto
   - ModuloFechamentoDto / PaginatedModuloResponseDto
   - ChecklistDto / ChecklistEvidenciaDto / PaginatedChecklistResponseDto
   - ConciliacaoDto / PaginatedConciliacaoResponseDto
   - ValidarAssinarFechamentoDto
   - PortalDto / AtividadePortalDto / PortalResumoEmpresaDto / PaginatedPortalResponseDto

   Enums vivos em domain/enums/closing.enums.ts:
   - FechamentoPeriodoStatus: ABERTO | EM_ANDAMENTO | CONCILIACAO | VALIDACAO |
     FECHADO | CANCELADO
   - ModuloFechamentoStatus: PENDENTE | EM_ANDAMENTO | CONCLUIDO | BLOQUEADO |
     NAO_APLICAVEL
   - ChecklistItemStatus: PENDENTE | EM_ANDAMENTO | CONCLUIDO | NAO_APLICAVEL
   - ConciliacaoStatus: CONCILIADO | DIVERGENTE | PENDENTE | IGNORADO
   - AtividadePortalTipo: FECHAMENTO_FISCAL | INTEGRACAO_CONTABIL | LOTE_GUIAS |
     DOCS_BAIXA | NOTAS_EMITIDAS

   Etapas do workflow (CLOSING_FLOW_STEPS): 1 Module Closure, 2 Bank
   Conciliation, 3 Tax Finalization, 4 Inventory Valuation,
   5 Accounting Consolidation.
   ═══════════════════════════════════════════════════════════════════════════ */

/* ── Enums ── */

export type FechamentoPeriodoStatus =
  | 'ABERTO'
  | 'EM_ANDAMENTO'
  | 'CONCILIACAO'
  | 'VALIDACAO'
  | 'FECHADO'
  | 'CANCELADO';

export type ModuloFechamentoStatus =
  | 'PENDENTE'
  | 'EM_ANDAMENTO'
  | 'CONCLUIDO'
  | 'BLOQUEADO'
  | 'NAO_APLICAVEL';

export type ChecklistItemStatus =
  | 'PENDENTE'
  | 'EM_ANDAMENTO'
  | 'CONCLUIDO'
  | 'NAO_APLICAVEL';

export type ConciliacaoStatus =
  | 'CONCILIADO'
  | 'DIVERGENTE'
  | 'PENDENTE'
  | 'IGNORADO';

export type AtividadePortalTipo =
  | 'FECHAMENTO_FISCAL'
  | 'INTEGRACAO_CONTABIL'
  | 'LOTE_GUIAS'
  | 'DOCS_BAIXA'
  | 'NOTAS_EMITIDAS';

export type ModuloChave = 'fiscal' | 'inventory' | 'financial' | 'accounting';

/* ── Paginação padrão do backend ── */

export interface Paginated<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

/* ── Período de fechamento ── */

export interface CriticalPendingIssue {
  id: string;
  titulo: string;
  descricao?: string;
  modulo?: string;
  severidade?: 'ALTA' | 'MEDIA' | 'BAIXA';
  criadoEm: string;
  resolvido?: boolean;
}

export interface FechamentoPeriodo {
  id: string;
  tenantId: string;
  companyId: string;
  branchId: string | null;
  ano: number;
  mes: number;
  status: FechamentoPeriodoStatus;
  masterPeriodLock: boolean;
  criticalPendingIssues: CriticalPendingIssue[];
  pendingCriticalCount: number;
  label: string;
  fechadoEm: string | null;
  fechadoPor: string | null;
  assinadoPor: string | null;
  cargoAssinante: string | null;
  assinadoEm: string | null;
  notaValidacao: string | null;
  createdAt: string;
  updatedAt: string;
}

/* ── Módulos da Matriz Brasil ── */

export interface ModuloFechamento {
  id: string;
  tenantId: string;
  companyId: string;
  periodId: string;
  modulo: string;
  status: ModuloFechamentoStatus;
  observacao: string | null;
  concluidoEm: string | null;
  concluidoPor: string | null;
  createdAt: string;
  updatedAt: string;
}

/* ── Dashboard Matriz Brasil (Painel de Controle) ── */

export interface DashboardModulo {
  modulo: string;
  status: ModuloFechamentoStatus;
  observacao: string | null;
  concluidoEm: string | null;
}

export interface DashboardPeriodo {
  id: string;
  ano: number;
  mes: number;
  status: FechamentoPeriodoStatus;
  masterPeriodLock: boolean;
  label: string;
  fechadoEm: string | null;
  assinadoPor: string | null;
  assinadoEm: string | null;
}

export interface ClosingDashboard {
  period: DashboardPeriodo;
  completionPercent: number;
  concluidos: number;
  totalModulos: number;
  modulos: DashboardModulo[];
  moduloStatusCounts: Record<string, number>;
  criticalPendingIssues: CriticalPendingIssue[];
  pendingCriticalCount: number;
}

export interface DashboardQuery {
  mes: number;
  ano: number;
}

/* ── Checklist do workflow ── */

export interface ChecklistEvidencia {
  tipo: 'LINK' | 'NOTA' | 'ARQUIVO';
  texto: string;
  criadoEm?: string;
}

export interface ChecklistItem {
  id: string;
  tenantId: string;
  companyId: string;
  periodId: string;
  etapa: string;
  ordem: number;
  item: string;
  status: ChecklistItemStatus;
  responsavel: string | null;
  evidencias: ChecklistEvidencia[];
  bankName: string | null;
  ledgerBalance: number | null;
  statementBalance: number | null;
  bankVariance: number | null;
  concluidoEm: string | null;
  concluidoPor: string | null;
  createdAt: string;
  updatedAt: string;
}

export type ChecklistAcao = 'INICIAR' | 'CONCLUIDO' | 'NAO_APLICAVEL' | 'REABRIR';

export interface DadosBancarios {
  bankName?: string | null;
  ledgerBalance?: number | null;
  statementBalance?: number | null;
}

export interface UpdateChecklistItemInput {
  acao: ChecklistAcao;
  por?: string;
  evidencias?: ChecklistEvidencia[];
  dadosBancarios?: DadosBancarios;
}

/* ── Conciliação fiscal × contábil ── */

export interface Conciliacao {
  id: string;
  periodId: string;
  accountCode: string;
  description: string | null;
  accountingLedger: number | null;
  taxRecord: number | null;
  variance: number | null;
  absVariance: number;
  temDivergencia: boolean;
  status: ConciliacaoStatus;
  observacao: string | null;
  tipoAjuste: 'ENTRY' | 'ADJUST' | null;
  ajusteProposto: number | null;
}

export interface UpdateConciliacaoInput {
  valores?: {
    accountingLedger?: number | null;
    taxRecord?: number | null;
    description?: string | null;
  };
  ajuste?: {
    tipoAjuste: 'ENTRY' | 'ADJUST';
    valor: number;
  };
  status?: Exclude<ConciliacaoStatus, 'PENDENTE'>;
  observacao?: string;
}

/* ── Validação final e assinatura (POST validate-sign) ── */

export interface ValidarAssinarInput {
  assinadoPor: string;
  cargoAssinante?: string;
  notaValidacao?: string;
  fechadoPor?: string;
}

/* ── Portal do Contador (multi-empresa) ── */

export interface AtividadePortal {
  id: string;
  tenantId: string;
  portalClienteId: string;
  tipo: AtividadePortalTipo;
  descricao: string;
  ocorridoEm: string;
}

export interface PortalCliente {
  id: string;
  tenantId: string;
  companyId: string;
  clienteCnpj: string | null;
  clienteNome: string | null;
  empresaNome: string | null;
  empresaCnpj: string | null;
  statusFiscal: string | null;
  notasEmitidas: number;
  pendencias: number;
  valorMovimento: number | null;
  ultimaAtividade: string | null;
  ultimaAtividadeEm: string | null;
  observacao: string | null;
  atividades: AtividadePortal[];
  createdAt: string;
  updatedAt: string;
}

export interface PortalResumoEmpresa {
  companyId: string;
  empresaNome: string | null;
  empresaCnpj: string | null;
  clientes: number;
  totalNotasEmitidas: number;
  totalPendencias: number;
}

export interface PaginatedClosingPortal extends Paginated<PortalCliente> {
  resumoPorEmpresa: PortalResumoEmpresa[];
}

/* ── Rótulos PT-BR (mesmo vocabulário do protótipo) ── */

export const STATUS_PERIODO_LABEL: Record<FechamentoPeriodoStatus, string> = {
  ABERTO: 'Aberto',
  EM_ANDAMENTO: 'Em andamento',
  CONCILIACAO: 'Conciliação',
  VALIDACAO: 'Validação',
  FECHADO: 'Fechado',
  CANCELADO: 'Cancelado',
};

export const STATUS_MODULO_LABEL: Record<ModuloFechamentoStatus, string> = {
  PENDENTE: 'Pendente',
  EM_ANDAMENTO: 'Em andamento',
  CONCLUIDO: 'Concluído',
  BLOQUEADO: 'Bloqueado',
  NAO_APLICAVEL: 'Não aplicável',
};

export const STATUS_CHECKLIST_LABEL: Record<ChecklistItemStatus, string> = {
  PENDENTE: 'Pendente',
  EM_ANDAMENTO: 'Em andamento',
  CONCLUIDO: 'Concluído',
  NAO_APLICAVEL: 'Não aplicável',
};

export const STATUS_CONCILIACAO_LABEL: Record<ConciliacaoStatus, string> = {
  CONCILIADO: 'Conciliado',
  DIVERGENTE: 'Divergente',
  PENDENTE: 'Pendente',
  IGNORADO: 'Ignorado',
};

/** Rótulos por módulo da Matriz Brasil (tela Painel de Controle). */
export const MODULO_ROTULO: Record<string, string> = {
  fiscal: 'Fiscal',
  inventory: 'Inventory',
  financial: 'Financial',
  accounting: 'Accounting',
};

/* ── Etapas do workflow de fechamento (checklist, 5 etapas) ── */

/** Ordem canônica do workflow — usada para agrupar/ordenar o checklist. */
export const CLOSING_FLOW_STEPS = [
  '1 Module Closure',
  '2 Bank Conciliation',
  '3 Tax Finalization',
  '4 Inventory Valuation',
  '5 Accounting Consolidation',
] as const;

/** Rótulo PT-BR de cada etapa (título + descrição de apoio). */
export const ETAPA_WORKFLOW_LABEL: Record<string, { titulo: string; descricao: string }> = {
  '1 Module Closure': {
    titulo: 'Encerramento de Módulos',
    descricao: 'Fechamento dos módulos fiscal, inventário, financeiro e contábil da Matriz Brasil.',
  },
  '2 Bank Conciliation': {
    titulo: 'Conciliação Bancária',
    descricao: 'Conferência dos saldos contábeis (Ledger) contra os extratos bancários (Statement).',
  },
  '3 Tax Finalization': {
    titulo: 'Finalização Fiscal',
    descricao: 'Encerramento das obrigações fiscais do período (apurações e guias).',
  },
  '4 Inventory Valuation': {
    titulo: 'Avaliação de Estoque',
    descricao: 'Valoração do inventário e ajustes de custo do período.',
  },
  '5 Accounting Consolidation': {
    titulo: 'Consolidação Contábil',
    descricao: 'Consolidação dos lançamentos e fechamento contábil do período.',
  },
};

/** Rótulo PT-BR dos tipos de atividade do Portal do Contador. */
export const ATIVIDADE_PORTAL_LABEL: Record<AtividadePortalTipo, string> = {
  FECHAMENTO_FISCAL: 'Fechamento fiscal',
  INTEGRACAO_CONTABIL: 'Integração contábil',
  LOTE_GUIAS: 'Lote de guias',
  DOCS_BAIXA: 'Docs de baixa',
  NOTAS_EMITIDAS: 'Notas emitidas',
};